import { Box, Flex } from 'components/box';
import { css } from '@emotion/react';
import { BannerInterface } from 'types/types';
import resourceUrl from '../../../../util/make-url';
import Head from 'next/head';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft as IconPrevious,
  ChevronRight as IconNext,
} from 'react-feather';
import Button from 'components/button';
import ThumbnailBullets from 'components/_shared/widgets/thumbnail-bullets';
import { Link } from 'components/configurable-routing';

// NOTE: direct usage of the theme is discouraged in most circumstances.
// DO NOT emulate the following without understanding what you're doing.
import { breakpointsPx } from 'themes/onedayonly';

const FADE_IN_TIME = 0.2;

interface CompleteBanner extends BannerInterface {
  desktopImage: NonNullable<BannerInterface['desktopImage']>;
  mobileImage: NonNullable<BannerInterface['mobileImage']>;
  tabletImage: NonNullable<BannerInterface['tabletImage']>;
}

const isValidCompleteBanner = (
  banner: BannerInterface
): banner is CompleteBanner =>
  !!banner.desktopImage && !!banner.mobileImage && !!banner.tabletImage;

interface BannerUrls {
  mobile: string;
  tablet: string;
  desktop: string;
}

const NavigationButton = ({
  variant,
  onClick,
}: {
  variant: 'left' | 'right';
  onClick: () => void;
}) => (
  <Button
    position="absolute"
    variant="flat"
    top="calc(50% - 28px)"
    left={variant === 'left' ? '0' : undefined}
    right={variant === 'right' ? '0' : undefined}
    height="fit-content"
    width="fit-content"
    p="1px"
    zIndex={10}
    color="white"
    shouldFocus={false}
    hoverColor="white"
    fontColor="white"
    onClick={onClick}
  >
    {variant === 'right' ? <IconNext size="50" /> : <IconPrevious size="50" />}
  </Button>
);

const BannerPreloads = ({ mobile, tablet, desktop }: BannerUrls) => (
  <Head>
    <link
      rel="preload"
      href={mobile}
      as="image"
      media={`(max-width: ${breakpointsPx[0] - 1}px)`}
    />

    <link
      rel="preload"
      href={tablet}
      as="image"
      media={`(min-width: ${breakpointsPx[0]}px) and (max-width: ${
        breakpointsPx[1] - 1
      }px)`}
    />

    <link
      rel="preload"
      href={desktop}
      as="image"
      media={`(min-width: ${breakpointsPx[1]}px)`}
    />
  </Head>
);

const getBannerImageAssetUrls = (banner: CompleteBanner): BannerUrls => {
  const mobile = resourceUrl(banner.mobileImage.url, {
    isAssetUrl: banner.isAssetUrl,
    quality: 75,
    width: 500,
  });

  const tablet = resourceUrl(banner.tabletImage.url, {
    isAssetUrl: banner.isAssetUrl,
    quality: 75,
    width: 900,
  });

  const desktop = resourceUrl(banner.desktopImage.url, {
    isAssetUrl: banner.isAssetUrl,
    quality: 75,
    width: 1200,
  });

  return { mobile, tablet, desktop };
};

const BannerImage = ({
  mobile,
  tablet,
  desktop,
  altText,
}: BannerUrls & { altText?: string }) => (
  <picture>
    <source srcSet={mobile} media={`(max-width: ${breakpointsPx[0] - 1}px)`} />
    <source
      srcSet={tablet}
      media={`(min-width: ${breakpointsPx[0]}px) and (max-width: ${
        breakpointsPx[1] - 1
      }px)`}
    />
    <img
      aria-label={altText}
      alt={altText}
      src={desktop}
      css={css`
        width: 100%;
        margin: 0;
        padding: 0;
      `}
    />
  </picture>
);

const BannerInner = ({
  bannerUrls,
  altText,
  link,
}: {
  bannerUrls: BannerUrls;
  altText?: string;
  link?: string;
}) => {
  if (link) {
    return (
      <Link href={link} title={altText} target="_blank">
        <BannerImage altText={altText} {...bannerUrls} />
      </Link>
    );
  }
  return <BannerImage altText={altText} {...bannerUrls} />;
};

const getBanner = (banners: CompleteBanner[], index: number) =>
  banners[index] || banners[0];

const BannerCarousel = ({ banners }: { banners: CompleteBanner[] }) => {
  const [bannerIndex, setBannerIndex] = useState(0);
  const numBanners = banners.length;
  const [delay, setDelay] = useState(5000);

  const assetUrls = useMemo(() => {
    const urls: Record<string, BannerUrls> = {};
    banners.forEach(banner => {
      urls[banner.id] = getBannerImageAssetUrls(banner);
    });
    return urls;
  }, [banners]);

  useEffect(() => {
    if (numBanners < 2) return;
    const timeoutId = setTimeout(() => {
      setBannerIndex(prevIndex => (prevIndex + 1) % numBanners);
      setDelay(5000);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [delay, numBanners]);

  const clickToBanner = useCallback((index: number) => {
    setBannerIndex(index);
    setDelay(10000);
  }, []);

  useEffect(() => {
    setBannerIndex(0);
  }, [banners]);

  const currentBanner = useMemo(() => getBanner(banners, bannerIndex), [
    banners,
    bannerIndex,
  ]);

  if (!currentBanner) return null;

  return (
    <Box key={banners.map(b => b.id).join('-')}>
      {banners.map(banner => (
        <BannerPreloads
          key={`banner-preloads-${banner.id}`}
          {...assetUrls[banner.id]}
        />
      ))}

      <Box
        tabIndex={0}
        m={0}
        p={0}
        position="relative"
        css={theme => css`
          border-radius: ${theme.radii[3]}px;
          overflow: hidden;
        `}
      >
        {numBanners > 1 && (
          <NavigationButton
            variant="left"
            onClick={() =>
              clickToBanner(
                bannerIndex === 0 ? numBanners - 1 : bannerIndex - 1
              )
            }
          />
        )}

        <BannerInner
          bannerUrls={assetUrls[currentBanner.id]}
          altText={currentBanner.altText}
          link={currentBanner.link}
        />

        {numBanners > 1 && (
          <NavigationButton
            variant="right"
            onClick={() =>
              clickToBanner(
                bannerIndex === numBanners - 1 ? 0 : bannerIndex + 1
              )
            }
          />
        )}
      </Box>

      {numBanners > 1 && (
        <Flex
          width="100%"
          justifyContent="center"
          mt="-0.9rem"
          css={css`
            animation: fadeIn ${FADE_IN_TIME}s;
            @keyframes fadeIn {
              0% {
                opacity: 0;
              }
              100% {
                opacity: 1;
              }
            }
          `}
        >
          <ThumbnailBullets
            size="small"
            showOnAllDevices
            total={numBanners}
            activeIndex={bannerIndex}
          />
        </Flex>
      )}
    </Box>
  );
};

const Banner = ({ banners: bannersProp }: { banners?: BannerInterface[] }) => {
  const banners = useMemo(
    () => (bannersProp || []).filter(isValidCompleteBanner),
    [bannersProp]
  );
  if (banners.length === 0) return null;

  return <BannerCarousel banners={banners} />;
};

export default Banner;
